import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import api from '../utils/api';

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    refresh:   <><path d="M1 4s0-3 3-3c3 0 5 2 5 2"/><path d="M1 4v4h4"/><path d="M15 12s0 3-3 3c-3 0-5-2-5-2"/><path d="M15 12v-4h-4"/></>,
    clipboard: <><rect x="9" y="2" width="6" height="4" rx="1" ry="1"/><path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/></>,
    wrench:    <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    printer:   <><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></>,
    scan:      <><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><line x1="7" y1="12" x2="17" y2="12"/></>,
    eyeoff:    <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>,
    shield:    <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    shieldok:  <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></>,
    info:      <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    user:      <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    clock:     <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    file:      <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
};

const BEHAVIORS = [
  { key: 'clipboard',  icon: 'clipboard', label: 'Copier-coller',  color: '#f59e0b', border: '#f59e0b' },
  { key: 'devtools',   icon: 'wrench',    label: 'DevTools',        color: '#8b5cf6', border: '#8b5cf6' },
  { key: 'print',      icon: 'printer',   label: 'Impression',      color: '#ef4444', border: '#ef4444' },
  { key: 'screenshot', icon: 'scan',      label: 'Capture écran',   color: '#10b981', border: '#10b981' },
  { key: 'focus_lost', icon: 'eyeoff',    label: 'Onglet quitté',   color: '#6b7280', border: '#6b7280' },
];

export default function BehaviorPage() {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.get('/track/events?limit=500');
      const behaviorEvents = data.filter(e =>
        e.event_type === 'view_progress' && e.metadata &&
        (e.metadata.clipboard || e.metadata.devtools || e.metadata.print ||
         e.metadata.focus_lost > 3 || e.metadata.screenshot)
      );
      setEvents(behaviorEvents);
      setLastUpdate(new Date());
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const filteredEvents = filter === 'all'
    ? events
    : events.filter(e => e.metadata?.[filter]);

  return (
    <div style={{ padding: 16, background: '#0a0a0a', minHeight: '100vh' }}>

      {/* Breadcrumb + titre */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
          Administration / Comportements
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: '#ffffff', letterSpacing: -1, marginBottom: 4 }}>
          Comportements <span style={{ color: '#d4f535' }}>suspects</span>
        </h1>
        <p style={{ color: '#555', fontSize: 12 }}>Espace de contrôle documentaire</p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 14, borderTop: '1px solid #1a1a1a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#d4f535', boxShadow: '0 0 8px #d4f53599' }} />
            <span style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Instantané · Capture du {format(lastUpdate, 'dd MMM. yyyy').toUpperCase()}
            </span>
          </div>
          <button onClick={fetchEvents} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #333', background: '#111', color: '#ffffff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Icon name="refresh" size={14} color="#ffffff" /> Actualiser
          </button>
        </div>
      </div>

      {/* Info bloc — signaux détectés */}
      <div style={{ background: '#0d1117', border: '1px solid #1e1e1e', borderLeft: '3px solid #4db8ff', borderRadius: 14, padding: 14, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Icon name="shield" size={14} color="#4db8ff" />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>Comportements suspects</span>
        </div>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 12 }}>Signaux navigateur · surveillance non connectée</div>
        <div style={{ fontSize: 10, color: '#444', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>INSTANTANÉ</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {BEHAVIORS.map(b => (
            <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#111', border: '1px solid #1e1e1e', borderRadius: 10, padding: '8px 10px' }}>
              <Icon name={b.icon} size={14} color={b.color} />
              <span style={{ fontSize: 12, color: b.color, fontWeight: 700 }}>{b.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 4 compteurs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        {BEHAVIORS.slice(0, 4).map(b => {
          const count = events.filter(e => e.metadata?.[b.key]).length;
          return (
            <div key={b.key} style={{ background: '#111', border: `1px solid ${count > 0 ? b.border + '44' : '#1e1e1e'}`, borderLeft: `3px solid ${count > 0 ? b.border : '#1e1e1e'}`, borderRadius: 14, padding: '14px 16px', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 12, right: 12 }}>
                <Icon name="info" size={14} color="#333" />
              </div>
              <Icon name={b.icon} size={20} color={count > 0 ? b.color : '#333'} />
              <div style={{ fontSize: 36, fontWeight: 900, color: count > 0 ? b.color : '#333', lineHeight: 1, margin: '8px 0 6px' }}>{count}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#ffffff' }}>{b.label}</div>
              <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>Événement dans la capture</div>
            </div>
          );
        })}
      </div>

      {/* Section événements */}
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, color: '#ffffff', marginBottom: 4 }}>Événements comportementaux</h2>
        <div style={{ fontSize: 10, color: '#444', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
          {filteredEvents.length === 0 ? 'AUCUN ÉVÉNEMENT VISIBLE' : `${filteredEvents.length} ÉVÉNEMENT${filteredEvents.length > 1 ? 'S' : ''}`}
        </div>

        {/* Filtres */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setFilter('all')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 20, border: 'none', background: filter === 'all' ? '#d4f535' : 'transparent', color: filter === 'all' ? '#0a0a0a' : '#555', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
            Tout
          </button>
          {BEHAVIORS.map(b => (
            <button key={b.key} onClick={() => setFilter(b.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 20, border: 'none', background: filter === b.key ? '#1a1a1a' : 'transparent', color: filter === b.key ? b.color : '#555', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <Icon name={b.icon} size={13} color={filter === b.key ? b.color : '#555'} />
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#555', fontSize: 13 }}>Chargement…</div>
      ) : filteredEvents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#111', border: '1px solid #1e1e1e', borderRadius: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: '#1a1a00', border: '1px solid #d4f53533', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <Icon name="shieldok" size={26} color="#d4f535" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#ffffff', marginBottom: 8 }}>Aucun comportement suspect dans la capture</div>
          <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6, marginBottom: 8 }}>
            Les quatre compteurs affichés sont à zéro. Aucun événement individuel n'est fourni.
          </div>
          <div style={{ fontSize: 11, color: '#444' }}>
            Cet instantané ne confirme pas la sécurité des accès actuels.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredEvents.map((e, i) => {
            const detected = BEHAVIORS.filter(b =>
              b.key === 'focus_lost' ? (e.metadata?.focus_lost > 3) : e.metadata?.[b.key]
            );
            const primary = detected[0];
            return (
              <div key={i} style={{ background: '#111', border: '1px solid #1e1e1e', borderLeft: `3px solid ${primary?.border || '#333'}`, borderRadius: 14, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {detected.map(b => (
                      <span key={b.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 6, background: '#1a1a1a', border: `1px solid ${b.border}44`, fontSize: 11, fontWeight: 700, color: b.color }}>
                        <Icon name={b.icon} size={11} color={b.color} />
                        {b.label}
                      </span>
                    ))}
                  </div>
                  <span style={{ fontSize: 10, color: '#444', fontFamily: 'monospace', whiteSpace: 'nowrap', marginLeft: 8 }}>
                    {e.created_at ? format(new Date(e.created_at), 'HH:mm') : '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888' }}>
                    <Icon name="user" size={12} color="#555" />
                    <span>{e.member_name || e.member_email || 'Inconnu'}</span>
                  </div>
                  {e.token && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#555' }}>
                      <Icon name="file" size={12} color="#444" />
                      <span style={{ fontFamily: 'monospace' }}>{e.token.slice(0, 8)}…</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Signaux mentionnés */}
      <div style={{ marginTop: 24, borderTop: '1px solid #1a1a1a', paddingTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ fontSize: 14, fontWeight: 800, color: '#ffffff' }}>Signaux mentionnés dans la référence</h3>
          <Icon name="info" size={14} color="#333" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {BEHAVIORS.map((b, i) => {
            const count = events.filter(e => e.metadata?.[b.key]).length;
            return (
              <div key={b.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 0', borderBottom: i < BEHAVIORS.length - 1 ? '1px solid #1a1a1a' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Icon name={b.icon} size={16} color={b.color} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>{b.label}</div>
                    <div style={{ fontSize: 11, color: '#555' }}>
                      {b.key === 'focus_lost' ? 'Compteur non fourni' : `${count} événement dans la capture`}
                    </div>
                  </div>
                </div>
                <Icon name="info" size={14} color="#333" />
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#444', marginTop: 14, lineHeight: 1.7 }}>
          Les signaux navigateur ne prouvent pas une intention malveillante. DevTools et captures d'écran ne sont pas détectables de façon exhaustive.
        </div>
      </div>

      {/* Note bas */}
      <div style={{ borderTop: '1px solid #1a1a1a', marginTop: 20, paddingTop: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name="shield" size={14} color="#333" />
        <div style={{ fontSize: 11, color: '#444', lineHeight: 1.6 }}>
          Données des captures uniquement · aucun suivi du presse-papiers, de l'impression, de l'écran ou des onglets.
        </div>
      </div>

    </div>
  );
}
